import { env } from '@/config/env'
import { AppError } from '@/lib/errors/appError'

export interface RequestOptions extends RequestInit {
  timeoutMs?: number
  /** Overrides the base URL this request resolves against — defaults to `env.NEXT_PUBLIC_API_URL`. */
  baseUrl?: string
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const {
    timeoutMs = 15_000,
    baseUrl = env.NEXT_PUBLIC_API_URL,
    signal: callerSignal,
    ...init
  } = options
  const timeoutController = new AbortController()
  const timeout = setTimeout(() => timeoutController.abort(), timeoutMs)
  // A caller-supplied signal (e.g. TanStack Query's `queryFn({ signal })`) must still be able to
  // cancel the request — merge it with the internal timeout instead of overwriting it.
  const signal = callerSignal
    ? AbortSignal.any([callerSignal, timeoutController.signal])
    : timeoutController.signal

  // `FormData` bodies (document uploads, the public funnel) need the browser to set its own
  // multipart boundary — a hardcoded 'application/json' would break the request.
  const isFormData = init.body instanceof FormData
  const headers = isFormData
    ? { ...init.headers }
    : { 'Content-Type': 'application/json', ...init.headers }

  try {
    const response = await fetch(`${baseUrl}${path}`, { ...init, signal, headers })

    if (!response.ok) {
      const body = await response.json().catch(() => null)
      throw AppError.fromResponse(response.status, body)
    }

    // A 204, or any other success status with no body, must not fall through to `response.json()`.
    if (response.status === 204) return undefined as T
    const text = await response.text()
    return (text ? JSON.parse(text) : undefined) as T
  } catch (error) {
    throw AppError.fromUnknown(error)
  } finally {
    clearTimeout(timeout)
  }
}
